import { type FC, useEffect, useCallback, useState, useMemo, useLayoutEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector, useMediaQuery } from '@core';
import {
  selectedSeatsSelector,
  schemasSelectors,
  seatsSelectors,
  urlParamsDataSelectors,
  uniquePricesSelectors,
  seatPreviewBuilderSelector,
  seatMapRulesSelector,
  isSeatResourcesLoadingSelector,
  hasSeatResourcesForSelector,
} from './store/selectors';
import { clearSelectedSeats, toggleSeatSelection, type SelectedSeat } from './store/slice';
import { getSeatResources } from './store/actions';
import { useNavigate } from 'react-router-dom';
import { markStepCompleted } from '../../core/navigate/store/slice';
import { AppRoutes } from '../../core/navigate/consts/routes.consts';
import { DateCarousel } from './components/DateCarousel';
import { PriceFilters } from './components/PriceFilters';
import CorporateOrderInfo from './components/CorporateOrderInfo';
import { setSelectedDate } from '../dateSelection/store/slice';
import SeatMapWebgl from '../canvasWebgl/SeatMapWebgl';
import { SeatCart } from './components/SeatCart';
import {
  DESKTOP_CART_RESERVED_HEIGHT,
  DESKTOP_SEAT_MAP_WIDTH,
  MOBILE_MAP_BOTTOM_GAP,
  MOBILE_SEAT_MAP_WIDTH,
} from './consts/layout';
import { DEFAULT_SEAT_COLORS } from '../canvasWebgl/constants/colors';
import { DESKTOP_STAGE_MAX_HEIGHT, MOBILE_STAGE_HEIGHT } from '../canvasWebgl/constants/seatMap';
import {
  fetchShowAndVenueInfo,
  isLoadingSelectors,
  selectedDateSelectors,
  showInfoSelectors,
} from '@modules-dateSelection';
import { toast } from 'sonner';
import { ClearSelectedSeatsDialog } from '@shared/ClearSelectedSeatsDialog';

export const SeatSelection: FC = () => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const mapViewportRef = useRef<HTMLDivElement | null>(null);
  const lastSeatResourcesRequestKeyRef = useRef<string | null>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Get data from Redux store
  const showInfo = useAppSelector(showInfoSelectors);
  const isShowInfoLoading = useAppSelector(isLoadingSelectors);
  const selectedSeats = useAppSelector(selectedSeatsSelector);
  const seatsData = useAppSelector(schemasSelectors);
  const seatsInfoData = useAppSelector(seatsSelectors);
  const schemaSvg = useAppSelector(state => state.seatSelection.schemaSvg);
  const selectedDate = useAppSelector(selectedDateSelectors);
  const urlParamsData = useAppSelector(urlParamsDataSelectors);
  const error = useAppSelector(state => state.seatSelection.error);
  const uniquePrices = useAppSelector(uniquePricesSelectors);
  const seatPreviewBuilder = useAppSelector(seatPreviewBuilderSelector);
  const eventId = selectedDate?.eventId || urlParamsData.eventsIds;
  const showId = urlParamsData.showIds;
  const seatMapRules = useAppSelector(seatMapRulesSelector);
  const isSeatResourcesLoading = useAppSelector(isSeatResourcesLoadingSelector);
  const hasSeatResources = useAppSelector(state =>
    hasSeatResourcesForSelector(state, { showId, eventId })
  );
  const isSoldOut = hasSeatResources && !isSeatResourcesLoading && seatsInfoData.length === 0;

  const isMobile = useMediaQuery('(max-width: 768px)');
  const hasShowBootstrap = showInfo?.id === showId;
  const seatMapWidth = isMobile ? MOBILE_SEAT_MAP_WIDTH : DESKTOP_SEAT_MAP_WIDTH;
  const [desktopPanelHeight, setDesktopPanelHeight] = useState(DESKTOP_STAGE_MAX_HEIGHT);
  const [desktopSeatMapHeight, setDesktopSeatMapHeight] = useState(DESKTOP_STAGE_MAX_HEIGHT);
  const [mobileSeatMapHeight, setMobileSeatMapHeight] = useState(MOBILE_STAGE_HEIGHT);
  const [pendingEventId, setPendingEventId] = useState<number | null>(null);
  const seatMapHeight = isMobile ? mobileSeatMapHeight : desktopSeatMapHeight;

  const [activePrices, setActivePrices] = useState<number[]>([]);

  const loadSeatResources = useCallback(
    (requestedEventId: number, refresh = false) => {
      if (!showId || !hasShowBootstrap || isSeatResourcesLoading) return;

      const deviceType = isMobile ? 'mobile' : undefined;
      const requestKey = `${showId}:${requestedEventId}:${deviceType ?? 'desktop'}`;
      if (!refresh && lastSeatResourcesRequestKeyRef.current === requestKey) return;

      lastSeatResourcesRequestKeyRef.current = requestKey;
      dispatch(getSeatResources({ showId, deviceType, eventId: requestedEventId }));
    },
    [dispatch, hasShowBootstrap, isMobile, isSeatResourcesLoading, showId]
  );

  const priceCategories = useMemo(
    () =>
      uniquePrices.map((price, index) => ({
        price,
        color: DEFAULT_SEAT_COLORS[index % DEFAULT_SEAT_COLORS.length],
        isActive: activePrices.includes(price),
      })),
    [uniquePrices, activePrices]
  );

  const handlePriceCategoryClick = useCallback((price: number) => {
    setActivePrices(current =>
      current.includes(price) ? current.filter(p => p !== price) : [...current, price]
    );
  }, []);

  // Handle seat selection
  const handleSeatSelect = useCallback(
    (seat: SelectedSeat) => {
      const previewUrl =
        seatPreviewBuilder?.({
          seatName: seat.seatName,
          seatNumber: seat.seatNumber,
          rowName: seat.rowName,
        }) ?? null;
      const seatWithPreview = previewUrl ? { ...seat, previewImageUrl: previewUrl } : seat;

      dispatch(toggleSeatSelection(seatWithPreview));
    },
    [dispatch, seatPreviewBuilder]
  );

  const handleProceedToCheckout = () => {
    if (!selectedSeats.length) {
      toast.error('Выберите места для оформления заказа');
      return;
    }

    const selectedEventIds = new Set(selectedSeats.map(seat => seat.eventId));
    if (selectedEventIds.size !== 1 || (eventId && !selectedEventIds.has(eventId))) {
      dispatch(clearSelectedSeats());
      toast.error('Выбранные места относятся к другому сеансу. Выберите места заново.');
      return;
    }

    dispatch(markStepCompleted(AppRoutes.SEATS_SELECTION));
    navigate(AppRoutes.CHECKOUT);
  };

  useLayoutEffect(() => {
    if (isMobile) return;

    const panelElement = panelRef.current;
    if (!panelElement) return;

    const updatePanelHeight = () => {
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const panelTop = panelElement.getBoundingClientRect().top;
      const nextHeight = Math.max(
        1,
        Math.floor(viewportHeight - panelTop - DESKTOP_CART_RESERVED_HEIGHT)
      );
      setDesktopPanelHeight(nextHeight);
    };

    updatePanelHeight();
    window.addEventListener('resize', updatePanelHeight);
    window.visualViewport?.addEventListener('resize', updatePanelHeight);

    return () => {
      window.removeEventListener('resize', updatePanelHeight);
      window.visualViewport?.removeEventListener('resize', updatePanelHeight);
    };
  }, [isMobile]);

  useLayoutEffect(() => {
    if (isMobile) return;

    const mapViewportElement = mapViewportRef.current;
    if (!mapViewportElement) return;

    const updateMapHeight = () => {
      const nextHeight = Math.max(1, Math.floor(mapViewportElement.getBoundingClientRect().height));
      setDesktopSeatMapHeight(nextHeight);
    };

    updateMapHeight();

    const observer = new ResizeObserver(() => {
      updateMapHeight();
    });
    observer.observe(mapViewportElement);

    return () => {
      observer.disconnect();
    };
  }, [desktopPanelHeight, isMobile]);

  useLayoutEffect(() => {
    if (!isMobile) return;

    const mapViewportElement = mapViewportRef.current;
    if (!mapViewportElement) return;

    const updateMobileMapHeight = () => {
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const mapTop = mapViewportElement.getBoundingClientRect().top;
      const nextHeight = Math.max(1, Math.floor(viewportHeight - mapTop - MOBILE_MAP_BOTTOM_GAP));
      const nextSeatMapHeight = Math.min(MOBILE_STAGE_HEIGHT, nextHeight);
      setMobileSeatMapHeight(nextSeatMapHeight);
    };

    updateMobileMapHeight();

    const observer = new ResizeObserver(() => {
      updateMobileMapHeight();
    });
    observer.observe(mapViewportElement);
    window.addEventListener('resize', updateMobileMapHeight);
    window.visualViewport?.addEventListener('resize', updateMobileMapHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateMobileMapHeight);
      window.visualViewport?.removeEventListener('resize', updateMobileMapHeight);
    };
  }, [isMobile]);

  const applyEventSelection = useCallback(
    (nextEventId: number, shouldClearSeats: boolean) => {
      const selectedEvent = showInfo?.events?.find(event => event.id === nextEventId);
      if (!selectedEvent) return;

      const eventDate = new Date(selectedEvent.from);
      dispatch(
        setSelectedDate({
          date: eventDate.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
          time: eventDate.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
          eventId: nextEventId,
        })
      );

      if (shouldClearSeats) {
        dispatch(clearSelectedSeats());
      }

      if (nextEventId === eventId) {
        loadSeatResources(nextEventId, true);
      }
    },
    [dispatch, eventId, loadSeatResources, showInfo]
  );

  const handleDateSelect = useCallback(
    (e: React.MouseEvent, nextEventId: number) => {
      e.preventDefault();
      e.stopPropagation();

      if (nextEventId !== eventId && selectedSeats.length > 0) {
        setPendingEventId(nextEventId);
        return;
      }

      applyEventSelection(nextEventId, false);
    },
    [applyEventSelection, eventId, selectedSeats.length]
  );

  const handleConfirmEventChange = () => {
    if (!pendingEventId) return;

    applyEventSelection(pendingEventId, true);
    setPendingEventId(null);
  };

  const handleRemoveSeat = (seatId: number) => {
    const seat = selectedSeats.find(s => s.seatId === seatId);
    if (seat) {
      dispatch(toggleSeatSelection(seat));
    }
  };

  useEffect(() => {
    if (!showId || hasShowBootstrap || isShowInfoLoading) return;

    dispatch(
      fetchShowAndVenueInfo({
        showId,
        step: eventId ? AppRoutes.SEATS_SELECTION : undefined,
      })
    );
  }, [dispatch, eventId, hasShowBootstrap, isShowInfoLoading, showId]);

  useEffect(() => {
    if (eventId) loadSeatResources(eventId);
  }, [eventId, loadSeatResources]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="text-lg text-status-error">Ошибка: {error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" style={{ overscrollBehavior: 'contain' }}>
      <div
        ref={panelRef}
        className={`bg-surface-card rounded-lg ${isMobile ? '' : 'flex flex-col min-h-0'}`}
        style={isMobile ? undefined : { height: desktopPanelHeight }}
      >
        <div className={isMobile ? undefined : 'shrink-0'}>
          {!isMobile && (
            <h3 className="text-2xl font-semibold mb-1">{showInfo?.shortName || showInfo?.name}</h3>
          )}
          <div className="flex justify-between items-start mb-3">
            <p>
              {selectedDate?.date} в {selectedDate?.time}
              {showInfo?.hallName ? (
                <>
                  , <span className="font-semibold">{showInfo.hallName}</span>
                </>
              ) : null}
              {showInfo?.address ? `, ${showInfo.address}` : ''}
            </p>
          </div>
          {/* <div className="hidden lg:block"> */}
          {showInfo && showInfo.events && showInfo.events.length > 0 && (
            <DateCarousel
              showInfo={showInfo}
              onDateSelect={handleDateSelect}
              selectedDate={selectedDate}
            />
          )}
          {/* </div> */}
          {hasSeatResources && (
            <PriceFilters
              items={priceCategories}
              activePrices={activePrices}
              isMobile={isMobile}
              onToggle={handlePriceCategoryClick}
            />
          )}
        </div>
        <div
          ref={mapViewportRef}
          className={`relative ${isMobile ? '' : 'min-h-0 flex-1'} rounded-lg overflow-hidden flex justify-center`}
          style={{ overscrollBehavior: 'contain' }}
        >
          {eventId && hasSeatResources && (
            <>
              <SeatMapWebgl
                onSeatSelect={handleSeatSelect}
                selectedSeats={selectedSeats}
                externalSeatsData={seatsData}
                seatsInfoData={seatsInfoData}
                uniquePrices={uniquePrices}
                canvasWidth={seatMapWidth}
                canvasHeight={seatMapHeight}
                schemaSvg={schemaSvg}
                seatMapRules={seatMapRules}
                activePrices={activePrices}
                showId={showId}
                eventId={eventId}
                isSoldOut={isSoldOut}
                hallName={showInfo?.hallName ?? null}
                address={showInfo?.address ?? null}
              />
              <CorporateOrderInfo hidden={false} />
            </>
          )}
        </div>
        <SeatCart onCheckout={handleProceedToCheckout} onRemoveSeat={handleRemoveSeat} />
      </div>
      <ClearSelectedSeatsDialog
        open={Boolean(pendingEventId)}
        title="Сменить сеанс?"
        description="При переключении сеанса билеты из корзины будут удалены."
        onCancel={() => setPendingEventId(null)}
        onConfirm={handleConfirmEventChange}
      />
    </div>
  );
};
